import { useEffect, useCallback, useMemo, useRef } from 'react'

interface KeyboardShortcutOptions {
  disabled?: boolean
  preventDefault?: boolean
  /** Ignores shortcut if an input, textarea, or contentEditable is focused. Defaults to `true`. */
  ignoreInputFocus?: boolean
  /** Timeout in ms between keys in a sequence. Defaults to 1000ms. */
  sequenceTimeout?: number
}

interface KeyStep {
  key: string
  metaKey: boolean
  ctrlKey: boolean
  shiftKey: boolean
  altKey: boolean
}

interface ParsedShortcut {
  steps: KeyStep[]
  isSequence: boolean
}

const MODIFIER_KEYS = new Set([
  'mod',
  'meta',
  'cmd',
  'ctrl',
  'control',
  'shift',
  'alt',
  'option',
])

function buildModifierFlags(
  modifierParts: string[],
  isMacPlatform: boolean,
): Omit<KeyStep, 'key'> {
  let meta = modifierParts.includes('meta') || modifierParts.includes('cmd')
  let ctrl = modifierParts.includes('ctrl') || modifierParts.includes('control')
  const shift = modifierParts.includes('shift')
  const alt = modifierParts.includes('alt') || modifierParts.includes('option')

  if (modifierParts.includes('mod')) {
    if (isMacPlatform) {
      meta = true
    } else {
      ctrl = true
    }
  }

  return { metaKey: meta, ctrlKey: ctrl, shiftKey: shift, altKey: alt }
}

function parseShortcut(
  shortcut: string,
  isMacPlatform: boolean,
): ParsedShortcut {
  const lowerShortcut = shortcut.toLowerCase()
  const parts = lowerShortcut.split('+').map((part) => part.trim())

  // Separate modifiers from regular keys
  const modifierParts = parts.filter((p) => MODIFIER_KEYS.has(p))
  const keyParts = parts.filter((p) => !MODIFIER_KEYS.has(p))

  // Build modifier flags (apply to first key only in sequences)
  const modFlags = buildModifierFlags(modifierParts, isMacPlatform)

  const firstKey = keyParts[0]
  if (!firstKey) {
    throw new Error('Shortcut must contain a key')
  }

  if (keyParts.length === 1) {
    // Single key: simultaneous behavior (current)
    return {
      steps: [{ key: firstKey, ...modFlags }],
      isSequence: false,
    }
  }

  // Multiple keys: sequence
  // First key gets modifiers, rest are plain keys
  const noModifiers = {
    metaKey: false,
    ctrlKey: false,
    shiftKey: false,
    altKey: false,
  }

  return {
    steps: keyParts.map((key, i) =>
      Object.assign({ key }, i === 0 ? modFlags : noModifiers),
    ),
    isSequence: true,
  }
}

/**
 * Custom hook to handle keyboard shortcuts globally.
 *
 * Supports both simultaneous shortcuts (mod+key) and key sequences (key1+key2).
 *
 * @example
 * // Simultaneous shortcut (modifier + key pressed together)
 * useKeyboardShortcut('mod+S', handleSave)
 *
 * // Key sequence (keys pressed in order: S then C)
 * useKeyboardShortcut('s+c', openSettings)
 *
 * // Sequence with modifier on first key (Cmd+S then C)
 * useKeyboardShortcut('mod+s+c', openChannelSettings)
 *
 * // With options
 * useKeyboardShortcut('mod+Enter', handleSubmit, {
 *   disabled: isSubmitting,
 *   ignoreInputFocus: false
 * })
 *
 * @param shortcut The keyboard shortcut string.
 *                 - Simultaneous: "mod+S", "Shift+Enter" (modifier + single key)
 *                 - Sequence: "s+c", "mod+s+c" (multiple non-modifier keys = sequence)
 *                 Use "mod" for the platform-agnostic Command/Control key (⌘ on Mac, Ctrl on PC).
 *                 Other modifiers: "Shift", "Alt" (or "Option"), "Ctrl", "Cmd" (or "Meta").
 * @param callback The function to execute when the shortcut is pressed.
 * @param options  Configuration options for the shortcut.
 *   @param options.disabled         If true, disables the shortcut. Default: false.
 *   @param options.preventDefault   If true, calls event.preventDefault() when the shortcut is triggered. Default: true.
 *   @param options.ignoreInputFocus If true, ignores the shortcut if an input, textarea, or contentEditable element is focused. Default: true.
 *   @param options.sequenceTimeout  Time in ms allowed between keys in a sequence. Default: 1000ms.
 */
function validateShortcut(shortcut: string): void {
  if (!shortcut || typeof shortcut !== 'string') {
    throw new Error('Shortcut must be a non-empty string')
  }
}

const DEFAULT_SEQUENCE_TIMEOUT = 1000

function matchesStep(event: KeyboardEvent, step: KeyStep): boolean {
  // Guard against undefined key (IME composition, autocomplete, browser extensions)
  if (!event.key) {
    return false
  }

  const eventKey = event.key.toLowerCase()
  return (
    eventKey === step.key &&
    event.metaKey === step.metaKey &&
    event.ctrlKey === step.ctrlKey &&
    event.shiftKey === step.shiftKey &&
    event.altKey === step.altKey
  )
}

export function useKeyboardShortcut(
  shortcut: string,
  callback: (event: KeyboardEvent) => void | Promise<void>,
  options: KeyboardShortcutOptions = {},
): void {
  const {
    disabled = false,
    preventDefault = true,
    ignoreInputFocus = true,
    sequenceTimeout = DEFAULT_SEQUENCE_TIMEOUT,
  } = options

  // Platform check at runtime
  const isMacPlatform =
    typeof window !== 'undefined' &&
    /Mac|iPod|iPhone|iPad/.test(window.navigator.platform)

  validateShortcut(shortcut)

  const parsed = useMemo(
    () => parseShortcut(shortcut, isMacPlatform),
    [shortcut, isMacPlatform],
  )

  // Sequence state tracking
  const sequenceIndexRef = useRef(0)
  const lastKeyTimeRef = useRef(0)

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (disabled) return

      if (ignoreInputFocus) {
        const target = event.target
        if (
          target instanceof HTMLElement &&
          (target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.isContentEditable)
        ) {
          return
        }
      }

      const now = Date.now()

      if (parsed.isSequence) {
        // Reset sequence if timeout exceeded
        if (
          sequenceIndexRef.current > 0 &&
          now - lastKeyTimeRef.current > sequenceTimeout
        ) {
          sequenceIndexRef.current = 0
        }

        const currentStep = parsed.steps[sequenceIndexRef.current]
        if (currentStep === undefined) {
          sequenceIndexRef.current = 0
          return
        }

        if (matchesStep(event, currentStep)) {
          if (preventDefault) {
            event.preventDefault()
          }
          lastKeyTimeRef.current = now
          sequenceIndexRef.current++

          // Completed sequence
          if (sequenceIndexRef.current >= parsed.steps.length) {
            sequenceIndexRef.current = 0
            void callback(event)
          }
        } else if (sequenceIndexRef.current > 0) {
          // Wrong key mid-sequence: reset
          sequenceIndexRef.current = 0
        }
      } else {
        // Simultaneous shortcut (single step)
        const currentStep = parsed.steps[0]
        if (currentStep === undefined) return

        if (matchesStep(event, currentStep)) {
          if (preventDefault) {
            event.preventDefault()
          }
          void callback(event)
        }
      }
    },
    [
      callback,
      disabled,
      preventDefault,
      ignoreInputFocus,
      parsed,
      sequenceTimeout,
    ],
  )

  useEffect(() => {
    if (disabled) {
      window.removeEventListener('keydown', handleKeyDown)
      return
    }

    window.addEventListener('keydown', handleKeyDown)
    return (): void => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown, disabled])
}
